/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {GenericObject} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {sortByKey} from '@ion/core/src/utils/ObjectUtil';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';
import {usePagination, UsePaginationResult} from './usePagination';
import {usePaginationPrefetch} from './usePaginationPrefetch';

// defines the UsePaginatedCTCSearch hook's result...
export type UsePaginatedCTCSearchResult = UseDefaultQueryResult & {
  result: any;
  error: Error | null;
  keys: string[];
  refresh: () => void;
  paging: UsePaginationResult;
};

// defines the UsePaginatedCTCSearch hook's props definitions...
export interface UsePaginatedCTCSearch {
  api: Function;
  apiParams: GenericObject;
  initialPage?: number;
  pageSize?: number;
  baseKey: QueryKey;
  staleTime?: number;
  keepPreviousData?: boolean;
  refetchInterval?: number | boolean;
};

/**
 * The UsePaginatedCTCSearch hook is responsible for handling the CTC paginated post search
 * along with React-Query. The hook initially fetches the current page and next page and loads
 * the next-next pages while the user navigating to the next page(pre-fetch one page in advance
 * to give a better user experience). This hook also provides paginates controls likely
 * switching pages, page size change, jump to page, and refresh the current to the
 * parent components.
 *
 * ```typescript
 *
 *  import {UsePaginatedCTCSearch} from '@ion/query/src/hooks/';
 *
 *  const {
 *    isLoading,
 *    result: {users},
 *    paging
 *  } = UsePaginatedCTCSearch({
 *    api      : loadUsers,
 *    apiParams: {accountId: 5099}
 *    baseKey  : QueryKey.GET_USERS
 *  });
 *
 * ```
 */
export const usePaginatedCTCSearch = ({
  api,
  apiParams,
  baseKey,
  initialPage = 1,
  pageSize = 20,
  staleTime = getStaleTime(0, false, false) as number,
  refetchInterval,
  keepPreviousData = false
}: UsePaginatedCTCSearch): UsePaginatedCTCSearchResult => {
  const queryClient                 = useQueryClient();
  const paging: UsePaginationResult = usePagination(initialPage, pageSize);

  const apiParamsKey = useMemo(() => {
    const sorted = sortByKey({
      ...apiParams,
      pageSize : paging.pageSize
    });
    return safeJSONStringify(sorted);
  }, [apiParams, paging.pageSize]);

  const [keyMeta, setKeyMeta] = useState({
    paramKey : apiParamsKey,
    page     : initialPage
  });

  useEffect(() => {
    paging.setTotal(0);
    setKeyMeta({
      paramKey : apiParamsKey,
      page     : initialPage
    });
    paging.gotoPage(initialPage);
  }, [apiParamsKey]);

  useEffect(() => {
    if (keyMeta.page !== paging.page) {
      setKeyMeta({
        paramKey : apiParamsKey,
        page     : paging.page
      });
    }
  }, [paging.page]);

  const keys = useMemo(() => (
    createKey(baseKey, keyMeta.paramKey, keyMeta.page)
  ), [baseKey, keyMeta]);

  usePaginationPrefetch({
    resolver : (page: number) => api({
      ...apiParams,
      pageId   : page,
      pageSize : paging.pageSize
    }),
    hasNext : paging.hasNext,
    page    : paging.page,
    keys,
    staleTime
  });

  const query = useQuery(keys, () => api({
    ...apiParams,
    pageId   : paging.page,
    pageSize : paging.pageSize
  }), {
    staleTime,
    refetchInterval,
    keepPreviousData
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(keys);
  }, [keys]);

  useEffect(() => {
    if (query.data) {
      paging.setTotal(query.data.totalCount ?? 0);
    }
  }, [query.data?.totalCount]);

  return createQueryResult(query, {
    result : query.data ?? {},
    error  : query.error ?? null,
    refresh,
    keys,
    paging
  }) as UsePaginatedCTCSearchResult;
};

export default usePaginatedCTCSearch;
